import { useState, useEffect } from 'react';
import { api } from '../api/client';
import { useSidebarExtra } from '../context/SidebarExtraContext';
import { useCsvActions } from '../context/CsvActionsContext';
import { useT } from '../i18n';
import { Resource, VRIOScore } from '../types';
import { ClipboardList, MousePointerClick } from 'lucide-react';

interface VRIORating {
  inimitability: VRIOScore | null;
  durability: VRIOScore | null;
  appropriability: VRIOScore | null;
  substitutability: VRIOScore | null;
  competitiveSuperiority: VRIOScore | null;
}

interface ResourceRatingStatus {
  hasRatings: boolean;
  isComplete: boolean;
}

// Dimension keys are also VRIORating data fields — labels/tooltips are resolved
// at render time via t(`vrio.dim.${key}.…`) / t(`vrio.tooltip.${key}.${idx}`).
const VRIO_DIMENSIONS: { key: keyof VRIORating; scores: VRIOScore[] }[] = [
  { key: 'inimitability', scores: [1, 2, 3, 4, 5] },
  { key: 'durability', scores: [1, 2, 3, 4, 5] },
  { key: 'appropriability', scores: [1, 2, 3, 4, 5] },
  { key: 'substitutability', scores: [1, 2, 3, 4, 5] },
  { key: 'competitiveSuperiority', scores: [1, 2, 3, 4, 5] },
];

function ResourceList({
  resources,
  selectedResource,
  onSelect,
  ratingStatus,
}: {
  resources: Resource[];
  selectedResource: Resource | null;
  onSelect: (res: Resource) => void;
  ratingStatus: Record<number, ResourceRatingStatus>;
}) {
  return (
    <div className="cap-list">
      {resources.map((res) => {
        const status = ratingStatus[res.id];
        const dotClass = status?.isComplete ? 'complete' : status?.hasRatings ? 'partial' : '';
        return (
          <div
            key={res.id}
            className={`cap-item ${selectedResource?.id === res.id ? 'active' : ''}`}
            onClick={() => onSelect(res)}
          >
            <span className={`cap-item-dot ${dotClass}`} />
            <span className="cap-item-title">{res.title}</span>
          </div>
        );
      })}
    </div>
  );
}

export function VRIOPage() {
  const { t } = useT();
  const { setSidebarExtra } = useSidebarExtra();
  const { setCsvActions } = useCsvActions();

  const [shortlistedResources, setShortlistedResources] = useState<Resource[]>([]);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [ratings, setRatings] = useState<VRIORating>({
    inimitability: null,
    durability: null,
    appropriability: null,
    substitutability: null,
    competitiveSuperiority: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [ratingStatus, setRatingStatus] = useState<Record<number, ResourceRatingStatus>>({});

  // Register sidebar extra content
  useEffect(() => {
    setSidebarExtra(
      <ResourceList
        resources={shortlistedResources}
        selectedResource={selectedResource}
        onSelect={setSelectedResource}
        ratingStatus={ratingStatus}
      />
    );
  }, [shortlistedResources, selectedResource, ratingStatus, setSidebarExtra]);

  // Cleanup sidebar on unmount
  useEffect(() => {
    return () => setSidebarExtra(null);
  }, [setSidebarExtra]);

  // Register CSV actions in topbar
  useEffect(() => {
    setCsvActions({
      downloadUrl: '/api/vrio/export-csv',
      downloadLabel: t('csv.exportCsv'),
    });
    return () => setCsvActions(null);
  }, [setCsvActions, t]);

  useEffect(() => {
    api.getShortlist()
      .then((data: any) => setShortlistedResources(data))
      .finally(() => setLoading(false));
  }, []);

  // Auto-select first resource
  useEffect(() => {
    if (shortlistedResources.length > 0 && !selectedResource) {
      setSelectedResource(shortlistedResources[0]);
    }
  }, [shortlistedResources]);

  // Load rating status for all resources
  useEffect(() => {
    const loadStatus = async () => {
      const statusMap: Record<number, ResourceRatingStatus> = {};
      await Promise.all(
        shortlistedResources.map(async (res) => {
          try {
            const data = await api.getVRIORating(res.id) as any;
            if (data) {
              const dims = [data.inimitability, data.durability, data.appropriability, data.substitutability, data.competitive_superiority];
              const hasAny = dims.some((d: any) => d !== null);
              const allDone = dims.every((d: any) => d !== null);
              statusMap[res.id] = { hasRatings: hasAny, isComplete: allDone };
            } else {
              statusMap[res.id] = { hasRatings: false, isComplete: false };
            }
          } catch {
            statusMap[res.id] = { hasRatings: false, isComplete: false };
          }
        })
      );
      setRatingStatus(statusMap);
    };

    if (shortlistedResources.length > 0) {
      loadStatus();
    }
  }, [shortlistedResources]);

  useEffect(() => {
    if (selectedResource) {
      loadRatings();
    }
  }, [selectedResource]);

  const loadRatings = async () => {
    if (!selectedResource) return;

    try {
      const data = await api.getVRIORating(selectedResource.id) as any;
      if (data) {
        setRatings({
          inimitability: data.inimitability,
          durability: data.durability,
          appropriability: data.appropriability,
          substitutability: data.substitutability,
          competitiveSuperiority: data.competitive_superiority,
        });
      } else {
        setRatings({
          inimitability: null,
          durability: null,
          appropriability: null,
          substitutability: null,
          competitiveSuperiority: null,
        });
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRatingChange = async (dimension: keyof VRIORating, score: VRIOScore) => {
    if (!selectedResource) return;

    const currentScore = ratings[dimension];
    const isToggleOff = currentScore === score;
    const newScore = isToggleOff ? null : score;

    const newRatings = { ...ratings, [dimension]: newScore };
    setRatings(newRatings);

    // Update status
    const dims = Object.values(newRatings);
    const hasAny = dims.some((d) => d !== null);
    const allDone = dims.every((d) => d !== null);
    setRatingStatus((prev) => ({
      ...prev,
      [selectedResource.id]: { hasRatings: hasAny, isComplete: allDone },
    }));

    setSaving(true);
    try {
      await api.setVRIORating(selectedResource.id, newRatings);
    } catch (err: any) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const calculateComposite = (r: VRIORating): number => {
    const values = [r.inimitability, r.durability, r.appropriability, r.substitutability, r.competitiveSuperiority].filter(
      (v) => v !== null
    ) as number[];
    if (values.length === 0) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
  };

  const ratedCount = Object.values(ratings).filter((v) => v !== null).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={{ height: 200 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (shortlistedResources.length === 0) {
    return (
      <div className="empty-state-card">
        <div className="empty-state">
          <div className="empty-state-icon"><ClipboardList /></div>
          <div className="empty-state-title">{t('vrio.empty.noShortlist.title')}</div>
          <div className="empty-state-desc">
            {t('vrio.empty.noShortlist.desc')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rating-page">
      {!selectedResource ? (
        <div className="empty-state-card">
          <div className="empty-state">
            <div className="empty-state-icon"><MousePointerClick /></div>
            <div className="empty-state-desc">{t('vrio.empty.selectResource')}</div>
          </div>
        </div>
      ) : (
        <>
          {/* Resource Detail Card */}
          <div className="detail-card detail-card--sticky">
            <div className="detail-card-header">
              <h4 className="detail-card-title">{selectedResource.title}</h4>
              <div className="detail-card-header-tags">
                <span className={`meta-tag badge-${selectedResource.type.toLowerCase()}`}>{selectedResource.type}</span>
                <span className="meta-tag">{selectedResource.function}</span>
              </div>
            </div>
            <div className="detail-card-body">
              <p className="detail-card-description" style={{ maxWidth: Math.min(900, Math.max(300, Math.ceil((selectedResource.description || '').length / 3 * 7))) }}>
                {selectedResource.description || t('vrio.noDescription')}
              </p>
              <span className="detail-card-meta">
                {t('vrio.dimensionsCount').replace('{n}', String(ratedCount))} {saving && <span className="spinner spinner-sm" style={{ display: 'inline-block', marginLeft: 4 }} />}
              </span>
            </div>
          </div>

          <div className="rating-page-content">
            {/* Composite Score */}
            <div className="composite-score mt-12 mb-16">
              <span className="text-sm text-muted">{t('vrio.composite')}</span>
              <span className="composite-value">{calculateComposite(ratings).toFixed(1)}</span>
              <div className="composite-bar">
                <div
                  className="composite-bar-fill"
                  style={{ width: `${(calculateComposite(ratings) / 5) * 100}%` }}
                />
              </div>
            </div>

            {/* VRIO Dimensions */}
            {VRIO_DIMENSIONS.map((dim) => (
              <div key={dim.key} className="rating-card-enhanced">
                <div className="rating-section-enhanced">
                  <div className="rating-section-header">
                    <span className="rating-section-label">{t(`vrio.dim.${dim.key}.label`)}</span>
                    <span className="rating-section-hint">{t(`vrio.dim.${dim.key}.desc`)}</span>
                  </div>
                  <div className="rating-options-row">
                    {dim.scores.map((s, idx) => (
                      <div key={s} className="rating-option-wrapper">
                        <button
                          type="button"
                          className={`rating-option-btn ${ratings[dim.key] === s ? 'selected' : ''} vrio-${idx}`}
                          onClick={() => handleRatingChange(dim.key, s)}
                        >
                          <span className="rating-option-value">{s}</span>
                          <span className="rating-option-label">{t(`vrio.dim.${dim.key}.score.${s}`)}</span>
                          <span className="rating-option-info">
                            i
                            <span className="rating-option-tooltip">
                              {t(`vrio.tooltip.${dim.key}.${idx}`)}
                            </span>
                          </span>
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
